/**
 * The top of ☰ › Your plan…: what you may buy, or what you hold and a way to
 * manage it. The approved text mock (2026-10-11), drawn:
 *
 *   PLAN
 *   Free                          [ Monthly | Yearly ]
 *   ┌ EOD ───────────┐  ┌ Live ───────────┐
 *   │ $14.99 / month │  │ $34.99 / month  │
 *   │ …              │  │ …               │
 *   │  [ Choose EOD ]│  │     [ Go Live ] │
 *   └────────────────┘  └─────────────────┘
 *
 * Subscribed, the cards give way to one line and **Manage billing…**, which is
 * Stripe's portal: cancelling, a new card and EOD → Live all happen there, and
 * reach us as a webhook like any other change.
 *
 * **SweeterTrade's first release** (mock 2026-10-23, taken forward 10-31): the
 * switch reads Quarterly | Yearly and opens on Yearly; a card whose first
 * period costs differently says "$1,500 today, then $495 / year"; and an MRI
 * yearly member sees one Pro card under their plan — the one-time charge, the
 * renewal date kept, and the next two renewals.
 *
 * Every amount and every word on a card comes from `GET /billing/plans`, which
 * reads the Stripe product — see `lib/billing.ts`. With billing unconfigured
 * the section is simply absent; the rest of the plan screen stands.
 */

import { useEffect, useRef, useState } from "react";

import { cancelPause, checkout, pausePlan, plans, portal, resumePlan, upgrade } from "../api/billing";
import {
  type BillingPlans,
  type BillingReturn,
  type Held,
  type Interval,
  type PauseStart,
  type PlanIntent,
  type Upgrade,
  cardsFor,
  formatAmount,
  formatRenewal,
  heldStatus,
  openingSide,
  pauseUsedUp,
  perInterval,
  switchSides,
} from "../lib/billing";
import { reread as rereadEntitlements } from "../lib/entitlements";

const SECTION: React.CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  color: "var(--qf-text-muted)",
  margin: "4px 0 2px",
};

const NOTE: React.CSSProperties = {
  color: "var(--qf-text-secondary)",
  fontSize: 12,
  lineHeight: 1.5,
};

const HEAD: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  margin: "2px 0 8px",
};

const CARDS: React.CSSProperties = { display: "flex", gap: 10, flexWrap: "wrap" };

const CARD: React.CSSProperties = {
  flex: "1 1 180px",
  display: "flex",
  flexDirection: "column",
  gap: 6,
  padding: 10,
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  background: "var(--qf-surface)",
};

/** The card a landing-page button chose. */
const CHOSEN: React.CSSProperties = { ...CARD, border: "2px solid var(--qf-accent)", padding: 9 };

const BUTTON: React.CSSProperties = {
  alignSelf: "flex-end",
  marginTop: "auto",
  padding: "5px 12px",
  border: "1px solid var(--qf-accent)",
  borderRadius: 4,
  background: "var(--qf-accent)",
  color: "var(--qf-background)",
  font: "inherit",
  cursor: "pointer",
};

const GHOST: React.CSSProperties = {
  ...BUTTON,
  alignSelf: "auto",
  background: "transparent",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
};

function switchButton(on: boolean): React.CSSProperties {
  return {
    padding: "2px 10px",
    border: "1px solid var(--qf-border)",
    background: on ? "var(--qf-accent)" : "transparent",
    color: on ? "var(--qf-background)" : "var(--qf-text)",
    font: "inherit",
    fontSize: 12,
    cursor: "pointer",
  };
}

/** How long to wait for the webhook after Stripe sends the browser back. */
const POLL_MS = 2000;
const POLL_TRIES = 10;
/** Reads after the portal: at once, then every 4 s, so ~16 s in all. */
const PORTAL_REREADS = 5;

export function BillingSection({
  returned,
  intent = null,
  onPlans,
}: {
  returned?: BillingReturn | null;
  /** **Picked, not bought**: the card is marked and its button focused, and
   *  Checkout still takes the click (`payments.md` § QuFolio went live). */
  intent?: PlanIntent | null;
  /** Each answer as it lands — the plan screen names the held plan from it. */
  onPlans?: (answer: BillingPlans | null) => void;
}) {
  const onPlansRef = useRef(onPlans);
  onPlansRef.current = onPlans;
  const [data, setData] = useState<BillingPlans | null>(null);
  // Null until picked: the side opens where `openingSide` says, once the offers are in.
  const [picked, setInterval_] = useState<Interval | null>(intent?.interval ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waiting, setWaiting] = useState(returned === "done");

  useEffect(() => {
    let alive = true;
    let tries = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      const answer = await plans();
      if (!alive) return;
      setData(answer);
      onPlansRef.current?.(answer);
      // **Back from a payment, the grant is the webhook's** and may land a beat
      // after the browser does. Poll until it shows, then re-read what the
      // account may do, so the rest of the app unlocks without a reload.
      if (returned === "done") {
        if (answer?.current) {
          setWaiting(false);
          void rereadEntitlements();
        } else if (++tries < POLL_TRIES) {
          timer = setTimeout(load, POLL_MS);
        } else {
          setWaiting(false);
        }
      } else if (returned === "portal") {
        // **Back from the portal, the change is the webhook's too** — an
        // upgrade, a downgrade or a cancel — and nothing on the URL says which,
        // so there is no answer to wait *for*. Re-read the plan and what the
        // account may do a few times across the window a webhook takes.
        void rereadEntitlements();
        if (++tries < PORTAL_REREADS) timer = setTimeout(load, POLL_MS * 2);
      }
    };
    void load();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [returned]);

  if (!data || !data.enabled) return null;

  const go = async (open: () => Promise<string>) => {
    setBusy(true);
    setError(null);
    try {
      window.location.assign(await open());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setBusy(false);
    }
  };

  /** A pause, a cancel or a resume: the answer is the plan, redrawn in place. */
  const act = async (call: () => Promise<BillingPlans>) => {
    setBusy(true);
    setError(null);
    try {
      const answer = await call();
      setData(answer);
      onPlansRef.current?.(answer);
      // The groups moved on the server; the rest of the app follows at once.
      void rereadEntitlements();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  const held = data.current;
  const status = held ? heldStatus(held) : null;
  const interval = openingSide(data.offers, picked);
  const cards = cardsFor(data.offers, interval);
  const sides = switchSides(data.offers);

  return (
    <>
      <div style={SECTION}>Plan</div>
      <div style={HEAD}>
        <span>{held ? held.name : "Free"}</span>
        {!held && sides.length > 1 && (
          <span role="group" aria-label="Billing period">
            {sides.map((side, i) => (
              <button
                key={side.interval}
                type="button"
                aria-pressed={interval === side.interval}
                style={{
                  ...switchButton(interval === side.interval),
                  borderRadius: i === 0 ? "4px 0 0 4px" : i === sides.length - 1 ? "0 4px 4px 0" : 0,
                }}
                onClick={() => setInterval_(side.interval)}
              >
                {side.label}
              </button>
            ))}
          </span>
        )}
      </div>

      {waiting && <div style={NOTE}>Payment received — your plan updates in a few seconds.</div>}
      {returned === "done" && !waiting && !held && (
        <div style={NOTE}>
          Payment received, but your plan has not updated yet. It will within a few
          minutes; reopen this screen to check.
        </div>
      )}
      {returned === "cancelled" && !held && (
        <div style={NOTE}>Checkout was cancelled. Nothing was charged.</div>
      )}

      {held ? (
        <>
          <div style={HEAD}>
            <span style={NOTE}>
              {held.name} · {formatAmount(held.amount, held.currency)} {perInterval(held.interval)}
            </span>
            <button type="button" style={GHOST} disabled={busy} onClick={() => void go(portal)}>
              Manage billing…
            </button>
          </div>
          {status && (
            <div style={{ ...NOTE, color: status.warn ? "var(--qf-warning, #c90)" : NOTE.color }}>
              {status.text}
            </div>
          )}
          <PauseControls held={held} busy={busy} act={act} />
          {(held.upgrades ?? []).length > 0 && (
            <div style={{ ...CARDS, marginTop: 8 }}>
              {held.upgrades!.map((up) => (
                <UpgradeCard key={up.plan} up={up} busy={busy} onUpgrade={() => void go(() => upgrade(up.plan))} />
              ))}
            </div>
          )}
        </>
      ) : (
        <div style={CARDS}>
          {cards.map((offer) => (
            <div key={offer.plan} style={offer.plan === intent?.plan ? CHOSEN : CARD}>
              <strong>{offer.name}</strong>
              {offer.firstAmount !== undefined && offer.firstAmount !== offer.amount ? (
                <span>
                  {formatAmount(offer.firstAmount, offer.currency)} <span style={NOTE}>today</span>
                  <br />
                  <span style={NOTE}>
                    then {formatAmount(offer.amount, offer.currency)} {perInterval(offer.interval)}
                  </span>
                </span>
              ) : (
                <span>
                  {formatAmount(offer.amount, offer.currency)}{" "}
                  <span style={NOTE}>{perInterval(offer.interval)}</span>
                </span>
              )}
              {offer.description && <span style={NOTE}>{offer.description}</span>}
              <button
                type="button"
                style={BUTTON}
                disabled={busy}
                autoFocus={offer.plan === intent?.plan}
                onClick={() => void go(() => checkout(offer.plan, offer.interval))}
              >
                {offer.cta}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* **Invoices outlive the plan they paid for.** Subscribed, Manage
          billing… reaches them; on Free this is the only way back to them. */}
      {!held && data.hasBilling && (
        <button
          type="button"
          style={{ ...GHOST, alignSelf: "flex-start", marginTop: 8 }}
          disabled={busy}
          onClick={() => void go(portal)}
        >
          Billing history…
        </button>
      )}

      {error && <div style={{ ...NOTE, color: "var(--qf-negative)" }}>{error}</div>}
    </>
  );
}

/**
 * One plan the held one may move to: charged today, the date kept, then the
 * next two renewals — "then $495 on 1 Jan 2027, $895 after", or one amount
 * when the two are the same.
 */
function UpgradeCard({ up, busy, onUpgrade }: { up: Upgrade; busy: boolean; onUpgrade: () => void }) {
  const date = up.renewsAt !== null ? formatRenewal(up.renewsAt) : null;
  const next = formatAmount(up.nextAmount, up.currency);
  const later = formatAmount(up.laterAmount, up.currency);
  return (
    <div style={CARD}>
      <strong>{up.name}</strong>
      {up.description && <span style={NOTE}>{up.description}</span>}
      <span>
        {formatAmount(up.amount, up.currency)} <span style={NOTE}>today</span>
      </span>
      {date && (
        <span style={NOTE}>
          Renewal date stays {date}.{" "}
          {up.nextAmount === up.laterAmount
            ? `Then ${later} ${perInterval(up.interval)}.`
            : `Then ${next} on ${date}, ${later} ${perInterval(up.interval)} after.`}
        </span>
      )}
      <button type="button" style={BUTTON} disabled={busy} onClick={onUpgrade}>
        Upgrade to {up.name}
      </button>
    </div>
  );
}

/**
 * Pause, under a held plan (`payments.md` § Pausing a plan, mock approved
 * 2026-10-13). Up to 30 calendar days a year; each pause runs for all the days
 * left, from today or from the next renewal, and the renewal moves by the days
 * paused. Every date here is the server's — this only says which is which.
 */
function PauseControls({
  held,
  busy,
  act,
}: {
  held: Held;
  busy: boolean;
  act: (call: () => Promise<BillingPlans>) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState<PauseStart>("today");
  const pause = held.pause;
  const offer = held.pauseOffer;

  if (pause?.state === "active") {
    return (
      <button type="button" style={PAUSE_BUTTON} disabled={busy} onClick={() => void act(resumePlan)}>
        Resume now
      </button>
    );
  }
  if (pause?.state === "scheduled") {
    return (
      <button type="button" style={PAUSE_BUTTON} disabled={busy} onClick={() => void act(cancelPause)}>
        Cancel pause
      </button>
    );
  }
  const usedUp = pauseUsedUp(offer);
  if (usedUp) return <div style={NOTE}>{usedUp}</div>;
  if (!offer || offer.refused !== null || offer.daysLeft <= 0) return null;

  if (!open) {
    return (
      <button type="button" style={PAUSE_BUTTON} disabled={busy} onClick={() => setOpen(true)}>
        Pause plan…
      </button>
    );
  }
  const today = offer.today ?? null;
  const renewal = offer.renewal ?? null;
  const chosen = (start === "today" ? today : renewal) ?? today ?? renewal;
  return (
    <div role="group" aria-label="Pause plan" style={PAUSE_PANEL}>
      <strong>
        Pause {held.name} for {chosen?.days ?? offer.daysLeft} days
      </strong>
      {(
        [
          ["today", "From today", today],
          ["renewal", "From renewal", renewal],
        ] as const
      ).map(([value, label, window]) =>
        window ? (
          <label key={value} style={NOTE}>
            <input
              type="radio"
              name="pause-start"
              checked={start === value}
              onChange={() => setStart(value)}
            />{" "}
            {label} — {formatRenewal(window.startsAt)} to {formatRenewal(window.endsAt)}
          </label>
        ) : null,
      )}
      {chosen && held.renewsAt !== null && (
        <span style={NOTE}>
          Your next charge moves from {formatRenewal(held.renewsAt)} to{" "}
          {formatRenewal(chosen.renewsAfter)}.
        </span>
      )}
      <span style={NOTE}>Nothing is refunded; the days you've paid for are kept.</span>
      <span style={NOTE}>
        {offer.daysLeft} of {offer.allowance ?? 30} pause days left for {offer.year}.
      </span>
      <span style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          style={{ ...BUTTON, alignSelf: "auto" }}
          disabled={busy || !chosen}
          onClick={() => chosen && void act(() => pausePlan(chosen.start)).then(() => setOpen(false))}
        >
          Pause
        </button>
        <button type="button" style={PAUSE_BUTTON} disabled={busy} onClick={() => setOpen(false)}>
          Keep my plan
        </button>
      </span>
    </div>
  );
}

const PAUSE_BUTTON: React.CSSProperties = {
  ...GHOST,
  alignSelf: "flex-start",
  marginTop: 8,
};

const PAUSE_PANEL: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  marginTop: 8,
  padding: 10,
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  background: "var(--qf-surface)",
};
